/**
 * Die SVN-Kennungen und ihre Zuordnung zu Personen.
 *
 * Eine SVN-Kennung ist der Benutzername, unter dem eine Revision eingecheckt
 * wurde — ausdrücklich **nicht dasselbe wie die ERP-Identität**, und über die
 * Zeit können es je Person mehrere sein.
 *
 * Die nicht zugeordneten stehen in einer **eigenen Liste** und zuoberst: Sie
 * sind das, was ein Administrator nachziehen soll. Zurückgehalten wird deshalb
 * nichts — eine Revision wird nie deshalb liegen gelassen, weil ihre Kennung
 * noch niemandem gehört.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { Button, Chip, DataTable, Dialog, FormGrid, FormRow, Modal, Select, StatusPill } from "../../design-system";
import type { AdminApi, Person, SvnIdentity } from "../../api/client";
import { StrongCell } from "../cells";
import { ListSection } from "../list";
import { Notice, describe, useLoaded } from "../loading";

/** Der Eintrag der Auswahl, der die Zuordnung wieder löst. */
const NOBODY = "Niemandem zugeordnet";

interface Register {
  identities: SvnIdentity[];
  people: Person[];
}

export function SvnIdentitiesSection({ client }: { client: AdminApi }): ReactElement {
  const { state, reload } = useLoaded<Register>(async () => ({
    identities: await client.svnIdentities(),
    // Dieser Abschnitt braucht die Personen und nicht die Auskunft über den
    // Chat-Zugang, die daneben mitkommt (#104): Zugeordnet wird eine Kennung, und
    // dazu gehört ein Name.
    people: (await client.people()).people,
  }));
  const [edited, setEdited] = useState<SvnIdentity | null>(null);

  const identities = state.kind === "loaded" ? state.value.identities : [];
  const people = state.kind === "loaded" ? state.value.people : [];
  const unassigned = identities.filter((one) => one.personId === null);
  const assigned = identities.filter((one) => one.personId !== null);

  const nameOf = (personId: number | null): string =>
    people.find((one) => one.id === personId)?.displayName ?? NOBODY;

  return (
    <>
      {/*
        Zwei Abschnitte und **kein** umschließender `<section>` je Abschnitt:
        `ListSection` beschriftet sich selbst, und eine gleichnamige Region um
        eine Region machte `getByRole("region", { name })` mehrdeutig.
      */}
      <ListSection
        title="Nicht zugeordnete SVN-Kennungen"
        glyph="id-card"
        first
        headerRight={
          /*
            Null offene Kennungen sind kein Zustand, sondern ein Zähler: Nichts
            steht an. Erst was offen ist, ist ein Zustand und trägt seine Farbe.
          */
          state.kind === "loaded" ? (
            unassigned.length > 0 ? (
              <StatusPill tone="amber">{`${unassigned.length} offen`}</StatusPill>
            ) : (
              <Chip>{`${unassigned.length} offen`}</Chip>
            )
          ) : null
        }
      >
        <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {state.kind === "loaded" && unassigned.length === 0 ? (
            <Notice text="Jede gesehene Kennung gehört jemandem." />
          ) : null}
        </div>

        {unassigned.length > 0 ? (
          <DataTable
            onRowClick={(_row, index) => setEdited(unassigned[index] ?? null)}
            columns={[
              { key: "identity", label: "Kennung", width: 260, maxWidth: 260 },
              { key: "hint", label: "", align: "right", width: 220 },
            ]}
            rows={unassigned.map((one) => ({
              cells: {
                identity: <StrongCell>{one.identity}</StrongCell>,
                hint: "in Revisionen gesehen",
              },
            }))}
          />
        ) : null}
      </ListSection>

      <ListSection
        title="Zugeordnete SVN-Kennungen"
        glyph="user"
        headerRight={
          state.kind === "loaded" ? <Chip>{`${assigned.length} zugeordnet`}</Chip> : null
        }
      >
        <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
          {state.kind === "loaded" && assigned.length === 0 ? (
            <Notice text="Noch ist keine Kennung zugeordnet." />
          ) : null}
        </div>

        {assigned.length > 0 ? (
          <DataTable
            onRowClick={(_row, index) => setEdited(assigned[index] ?? null)}
            columns={[
              { key: "identity", label: "Kennung", width: 260, maxWidth: 260 },
              { key: "person", label: "Person", width: 240, maxWidth: 240 },
            ]}
            rows={assigned.map((one) => ({
              cells: {
                identity: <StrongCell>{one.identity}</StrongCell>,
                person: nameOf(one.personId),
              },
            }))}
          />
        ) : null}
      </ListSection>

      {edited === null ? null : (
        <AssignmentDialog
          identity={edited}
          people={people}
          save={(personId) => client.assignSvnIdentity(edited.id, personId)}
          close={() => setEdited(null)}
          saved={() => {
            setEdited(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function AssignmentDialog({
  identity,
  people,
  save,
  close,
  saved,
}: {
  identity: SvnIdentity;
  people: Person[];
  save: (personId: number | null) => Promise<void>;
  close: () => void;
  saved: () => void;
}): ReactElement {
  const [personId, setPersonId] = useState<number | null>(identity.personId);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const chosen = people.find((one) => one.id === personId)?.displayName ?? NOBODY;

  const submit = (): void => {
    setBusy(true);
    setError(null);

    save(personId).then(saved, (failure: unknown) => {
      setBusy(false);
      setError(describe(failure));
    });
  };

  return (
    <Modal onClose={close}>
      <Dialog
        title="SVN-Kennung zuordnen"
        description={`„${identity.identity}" ist der Benutzername im SVN, nicht die ERP-Identität. Einer Person können mehrere Kennungen gehören.`}
        width={520}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button variant="primary" size="lg" disabled={busy} onClick={submit}>
              {busy ? "Wird gespeichert ..." : "Speichern"}
            </Button>
          </>
        }
      >
        <FormGrid labelWidth="var(--grid-label-col)">
          <FormRow label="Person">
            <Select
              options={[NOBODY, ...people.map((one) => one.displayName)]}
              value={chosen}
              onSelect={(name) =>
                setPersonId(name === NOBODY ? null : (people.find((one) => one.displayName === name)?.id ?? null))
              }
            />
          </FormRow>

          {error === null ? null : (
            <FormRow full>
              <Notice text={error} glyph="triangle-alert" color="var(--ac-red)" />
            </FormRow>
          )}
        </FormGrid>
      </Dialog>
    </Modal>
  );
}
